// Recording upload orchestration — mixin applied to the ApiService prototype chain.
// The heavy S3 upload drivers (single-PUT, merge-to-key, and >5 GB multipart with a
// concurrency-limited XHR worker pool, progress aggregation, and abort handling).
// Split out of courseContentApi to keep each file focused; `this` binds to the
// ApiService instance, so calls to this.initiateMultipartUpload / completeMultipartUpload
// / abortMultipartUpload / createCourseRecordingS3Upload resolve through the chain.
const withRecordingUploadApi = (Base) => class extends Base {
  /**
   * Upload one file to a specific S3 key (single PUT or multipart depending on size).
   * Used internally by the merge-upload flow.
   */
  async uploadCourseRecordingToKey(courseId, file, key, onUploadProgress, options = {}) {
    const { signal } = options;
    const MULTIPART_THRESHOLD = 100 * 1024 * 1024;
    if (file.size > MULTIPART_THRESHOLD) {
      // Multipart with key override — uses the same orchestrator as the
      // legacy single-file multipart flow, but supplies the target key.
      const init = await this.initiateMultipartUpload(courseId, file, null, key);
      if (!init?.success) throw new Error(init?.message || 'Failed to initiate multipart upload');
      const { uploadId, partSize, partCount, parts, key: returnedKey, objectUrl } = init;
      return this._driveMultipartUpload(courseId, file, returnedKey, uploadId, partSize, partCount, parts, objectUrl, onUploadProgress, signal);
    } else {
      const presignResponse = await this.createCourseRecordingS3Upload(courseId, file, null, key);
      if (!presignResponse?.success) throw new Error(presignResponse?.message || 'Failed to prepare upload');
      await this.uploadToS3(presignResponse.uploadUrl, file, presignResponse.headers, onUploadProgress, signal);
      return { success: true, key: presignResponse.key, objectUrl: presignResponse.objectUrl };
    }
  }

  // Internal: drive a multipart upload that's already been initiated.
  async _driveMultipartUpload(courseId, file, key, uploadId, partSize, partCount, parts, objectUrl, onUploadProgress, signal) {
    const MAX_CONCURRENCY = 4;
    const partResults = new Array(partCount);
    const partProgress = new Array(partCount).fill(0);
    const activeRequests = new Set();
    let cancelled = false;
    let abortedByUser = false;

    // Allow an external signal (Cancel button / dropped connection handler) to
    // stop the in-flight parts and abort the multipart session.
    const onAbortSignal = () => {
      abortedByUser = true;
      cancelled = true;
      activeRequests.forEach((xhr) => xhr.abort());
    };
    if (signal) {
      if (signal.aborted) onAbortSignal();
      else signal.addEventListener('abort', onAbortSignal, { once: true });
    }
    const reportProgress = () => {
      if (typeof onUploadProgress !== 'function') return;
      const loaded = partProgress.reduce((sum, n) => sum + n, 0);
      const percent = file.size > 0 ? Math.round((loaded / file.size) * 100) : 0;
      onUploadProgress({ percent, loaded, total: file.size });
    };
    const uploadOnePart = (index) => {
      if (cancelled) return Promise.resolve();
      const { partNumber, url } = parts[index];
      const start = index * partSize;
      const end = Math.min(start + partSize, file.size);
      const blob = file.slice(start, end);
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        activeRequests.add(xhr);
        xhr.open('PUT', url);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) { partProgress[index] = e.loaded; reportProgress(); }
        };
        xhr.onload = () => {
          activeRequests.delete(xhr);
          if (cancelled) return resolve();
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
            if (!etag) return reject(new Error(`Part ${partNumber}: missing ETag (CORS?)`));
            partProgress[index] = blob.size; reportProgress();
            partResults[index] = { PartNumber: partNumber, ETag: etag.replace(/"/g, '') };
            resolve();
          } else reject(new Error(`Part ${partNumber} status ${xhr.status}`));
        };
        xhr.onerror = () => {
          activeRequests.delete(xhr);
          if (cancelled) return resolve();
          reject(new Error(`Part ${partNumber} network error`));
        };
        xhr.onabort = () => {
          activeRequests.delete(xhr);
          resolve();
        };
        xhr.send(blob);
      });
    };
    let cursor = 0;
    const workers = Array.from({ length: Math.min(MAX_CONCURRENCY, partCount) }, async () => {
      while (true) {
        if (cancelled) break;
        const idx = cursor++;
        if (idx >= partCount) break;
        await uploadOnePart(idx);
      }
    });
    try {
      await Promise.all(workers);
    } catch (err) {
      cancelled = true;
      activeRequests.forEach((xhr) => xhr.abort());
      try { await this.abortMultipartUpload(courseId, key, uploadId); } catch (e) { /* ignore */ }
      throw err;
    }
    // User cancelled (or the connection dropped) mid-upload: abort the session
    // so no object is assembled, and surface an AbortError to the caller.
    if (abortedByUser) {
      try { await this.abortMultipartUpload(courseId, key, uploadId); } catch (e) { /* ignore */ }
      const abortError = new Error('Upload cancelled.');
      abortError.name = 'AbortError';
      throw abortError;
    }
    const completeRes = await this.completeMultipartUpload(courseId, key, uploadId, partResults);
    if (!completeRes?.success) {
      try { await this.abortMultipartUpload(courseId, key, uploadId); } catch (e) { /* ignore */ }
      throw new Error(completeRes?.message || 'Failed to complete multipart upload');
    }
    return { success: true, key, objectUrl };
  }

  /**
   * Upload a recording via S3 multipart. Splits the file into chunks, uploads
   * each in parallel (up to MAX_CONCURRENCY at a time), aggregates progress,
   * and finalizes the multipart upload.
   *
   * Returns { success, objectUrl, key } on success. On any part failure, aborts
   * the multipart upload server-side before re-throwing.
   */
  async uploadCourseRecordingMultipart(courseId, file, recordingDate, onUploadProgress, options = {}) {
    const { signal, onInit, allowOverwrite = false, fileNameSuffix } = options;
    const MAX_CONCURRENCY = 4;

    // 1. Initiate
    const init = await this.initiateMultipartUpload(courseId, file, recordingDate, undefined, allowOverwrite, fileNameSuffix);
    if (!init?.success) {
      const message = init?.message || 'Failed to initiate multipart upload';
      const err = new Error(message);
      err.serverResponse = init;
      throw err;
    }
    const { uploadId, partSize, partCount, parts, key, objectUrl } = init;
    // Surface the destination key + uploadId as soon as they're known so the
    // caller can clean them up if the upload is cancelled or interrupted.
    if (typeof onInit === 'function') onInit({ key, uploadId });

    // 2. Upload all parts in parallel (bounded concurrency).
    const partResults = new Array(partCount);
    const partProgress = new Array(partCount).fill(0);
    const activeRequests = new Set();
    let cancelled = false;
    let abortedByUser = false;

    // Allow an external signal (Cancel button / dropped connection handler) to
    // stop the in-flight parts and abort the multipart session.
    const onAbortSignal = () => {
      abortedByUser = true;
      cancelled = true;
      activeRequests.forEach((xhr) => xhr.abort());
    };
    if (signal) {
      if (signal.aborted) onAbortSignal();
      else signal.addEventListener('abort', onAbortSignal, { once: true });
    }

    const reportProgress = () => {
      if (typeof onUploadProgress !== 'function') return;
      const loaded = partProgress.reduce((sum, n) => sum + n, 0);
      const percent = file.size > 0 ? Math.round((loaded / file.size) * 100) : 0;
      onUploadProgress({ percent, loaded, total: file.size });
    };

    const uploadOnePart = (index) => {
      if (cancelled) return Promise.resolve();
      const { partNumber, url } = parts[index];
      const start = index * partSize;
      const end = Math.min(start + partSize, file.size);
      const blob = file.slice(start, end);

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        activeRequests.add(xhr);
        xhr.open('PUT', url);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            partProgress[index] = e.loaded;
            reportProgress();
          }
        };
        xhr.onload = () => {
          activeRequests.delete(xhr);
          if (cancelled) return resolve();
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
            if (!etag) {
              reject(new Error(`Part ${partNumber}: missing ETag in response (check bucket CORS exposes ETag header)`));
              return;
            }
            partProgress[index] = blob.size;
            reportProgress();
            partResults[index] = { PartNumber: partNumber, ETag: etag.replace(/"/g, '') };
            resolve();
          } else {
            reject(new Error(`Part ${partNumber} failed with status ${xhr.status}: ${xhr.responseText?.slice(0, 200) || ''}`));
          }
        };
        xhr.onerror = () => {
          activeRequests.delete(xhr);
          if (cancelled) return resolve();
          reject(new Error(`Part ${partNumber} network error`));
        };
        xhr.ontimeout = () => {
          activeRequests.delete(xhr);
          if (cancelled) return resolve();
          reject(new Error(`Part ${partNumber} timed out`));
        };
        xhr.onabort = () => {
          activeRequests.delete(xhr);
          resolve();
        };
        xhr.send(blob);
      });
    };

    // Concurrency-limited worker pool
    let cursor = 0;
    const workers = Array.from({ length: Math.min(MAX_CONCURRENCY, partCount) }, async () => {
      while (true) {
        if (cancelled) break;
        const idx = cursor++;
        if (idx >= partCount) break;
        await uploadOnePart(idx);
      }
    });

    try {
      await Promise.all(workers);
    } catch (uploadErr) {
      cancelled = true;
      activeRequests.forEach((xhr) => xhr.abort());
      // Best-effort abort on the server. Don't swallow original error.
      try {
        await this.abortMultipartUpload(courseId, key, uploadId);
      } catch (e) { /* lifecycle rule will clean up */ }
      throw uploadErr;
    }

    // User cancelled (or the connection dropped) mid-upload: abort the session
    // so no object is assembled, and surface an AbortError to the caller.
    if (abortedByUser) {
      try {
        await this.abortMultipartUpload(courseId, key, uploadId);
      } catch (e) { /* lifecycle rule will clean up */ }
      const abortError = new Error('Upload cancelled.');
      abortError.name = 'AbortError';
      throw abortError;
    }

    // 3. Complete
    const completeRes = await this.completeMultipartUpload(courseId, key, uploadId, partResults);
    if (!completeRes?.success) {
      // Server-side complete failed; abort to clean up the multipart session
      try {
        await this.abortMultipartUpload(courseId, key, uploadId);
      } catch (e) { /* ignore */ }
      const err = new Error(completeRes?.message || 'Failed to complete multipart upload');
      err.serverResponse = completeRes;
      throw err;
    }

    return { success: true, key, objectUrl };
  }

  async uploadToS3(uploadUrl, file, headers = {}, onUploadProgress, signal) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        const abortError = new Error('Upload cancelled.');
        abortError.name = 'AbortError';
        reject(abortError);
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
      // Allow the caller (e.g. a Cancel button) to abort the in-flight PUT.
      const onAbortSignal = () => xhr.abort();
      if (signal) signal.addEventListener('abort', onAbortSignal, { once: true });
      const cleanupSignal = () => { if (signal) signal.removeEventListener('abort', onAbortSignal); };
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onUploadProgress) {
          onUploadProgress({ percent: Math.round((event.loaded / event.total) * 100), loaded: event.loaded, total: event.total });
        }
      };
      xhr.onload = () => {
        cleanupSignal();
        if (xhr.status >= 200 && xhr.status < 300) resolve({ success: true });
        else {
          const responseText = xhr.responseText ? this.sanitizePresignedUrl(xhr.responseText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()) : '';
          reject(new Error(`S3 upload failed with status ${xhr.status}${responseText ? `: ${responseText}` : ''}`));
        }
      };
      xhr.onerror = () => {
        cleanupSignal();
        let host = 'S3';
        try {
          host = new URL(uploadUrl).host;
        } catch (error) {}
        const networkError = new Error(`S3 upload failed before the server responded. Check CORS on ${host} and confirm the bucket allows PUT from this site.`);
        networkError.isNetworkError = true;
        reject(networkError);
      };
      xhr.onabort = () => {
        cleanupSignal();
        const abortError = new Error('Upload cancelled.');
        abortError.name = 'AbortError';
        reject(abortError);
      };
      xhr.send(file);
    });
  }
};

export default withRecordingUploadApi;
